import { LitElement, html } from 'lit';
import fsTypography from '@cof/fs-typography-core/lib/typography.lit.min.js';
import { TagKey } from '@cof/tag-catalog-fs-vehicle-and-loan-mfe';
import { DebugService } from '../../shared/services/debug.service.js';
import { style } from './delayed-decline.style.js';
import { VehicleService } from '../../services/vehicle.service.js';
import { LoanService } from '../../services/loan.service.js';
import '../iconic-container/iconic-container.component.js';
import '../iconic-vehicle-loan/iconic-vehicle-loan-card/iconic-vehicle-loan-card.component.js';
import '../iconic-vehicle/iconic-vehicle-card/iconic-vehicle-card.component.js';
import '../iconic-loan/iconic-loan-card/iconic-loan-card.component.js';
import { VehicleAndLoanService } from '../../services/vehicle-and-loan.service.js';
import { REFI_VEHICLE_LOAN_CONST } from '../../shared/constants/app.constants.js';
import { POLICY_LIMITS_CONST } from '../../shared/services/policy-limits.service.js';
import { ApplicationService } from '../../services/application.service.js';
import { currencyFormatter } from '../../shared/formatters/currency.formatter.js';
import { stratum } from '../../../constants/stratum.js';

export class RefiDelayedDecline extends LitElement {
  constructor() {
    super();
    this.debug = new DebugService('RefiDelayedDecline');
    this.vehicle = null;
    this.loan = null;
    this.header = '';
    this.subheader = '';
  }

  firstUpdated() {
    stratum.publishTag(
      TagKey.PAGE_VIEW_COAF_REFI_PREQUALIFY_VEHICLE_LOAN_EXIT_2
    );
  }

  connectedCallback() {
    super.connectedCallback();
    this.vehicle = VehicleService.selectedVehicle;
    this.loan = LoanService.isManualLoan
      ? ApplicationService.selectedLoan
      : LoanService.selectedLoan;
    this.debug.log(
      `Delayed decline rendered with: ${this.vehicle.year} ${this.vehicle.make} ${this.vehicle.model} and ${this.loan.lenderName}`
    );
    this.dispatchEvent(
      new CustomEvent('show-progress-bar', {
        detail: {
          displayBar: false,
        },
        composed: true,
        bubbles: true,
      })
    );
    this.determineDeclineReason();
  }

  static properties = {
    vehicle: { type: Object },
    loan: { type: Object },
  };

  static styles = [style, fsTypography];

  render() {
    return html`
      <refi-track-pageview pagename="ineligible"></refi-track-pageview>
      <div class="spacer"></div>
      <refi-iconic-container
        .headerText="${this.header}"
        ctaButtonText="Return to Capital One"
        @cta-clicked="${this.exitPrequal}"
      >
        <div class="subheader">
          ${this.subheader} Don't worry, this
          <b>did not impact your credit score.</b>
        </div>
        <div class="card">
          <refi-iconic-vehicle-loan-card
            .vehicle="${this.vehicle}"
            .loan="${this.loan}"
            vehicleAndLoanIndex="1"
            .showButton="${false}"
            hide-ineligibility
            .canEdit="${true}"
            .tagBalance="${false}"
          ></refi-iconic-vehicle-loan-card>
        </div>
      </refi-iconic-container>
    `;
  }

  determineDeclineReason() {
    this.header = `We can't refinance this car for you`;
    const declineReasons = REFI_VEHICLE_LOAN_CONST.IN_APP_DECLINE_REASONS;
    const declineReason = VehicleAndLoanService.getInAppDeclineReason(
      this.vehicle,
      this.loan
    );
    if (
      [
        'NO_WHOLESALE_VALUE',
        'MONTHLY_PAYMENT',
        'ATF',
        'INELIGIBLE_TERMS',
      ].includes(declineReason)
    ) {
      this.header += ` right now`;
    }
    this.subheader = '';
    if (declineReason) {
      const reasonFunction = declineReasons[declineReason];
      switch (declineReason) {
        case 'YEAR':
          this.subheader = reasonFunction(VehicleService.getMinVehicleYear());
          break;
        case 'INELIGIBLE_TERMS':
          this.subheader = reasonFunction(
            POLICY_LIMITS_CONST.MIN_REMAINING_TERM(),
            POLICY_LIMITS_CONST.MAX_REMAINING_TERM()
          );
          break;
        case 'MONTHLY_PAYMENT':
          this.subheader = reasonFunction(
            currencyFormatter(POLICY_LIMITS_CONST.MAX_MONTHLY_PAYMENT())
          );
          break;
        case 'ATF':
          this.subheader = reasonFunction(
            currencyFormatter(LoanService.getMinimumBalanceAmount()),
            currencyFormatter(LoanService.getMaximumBalanceAmount())
          );
          break;
        default:
          this.subheader = reasonFunction();
          break;
      }
      stratum.publishTag(TagKey.LOAN_LOOKUP_INELIGIBLE_VEHICLE_LOAN_REASON, {
        replacements: {
          value: declineReason,
        },
      });
    }
  }

  goToVehicle() {
    this.dispatchEvent(
      new CustomEvent('go-to-vehicle', {
        bubbles: true,
        composed: true,
      })
    );
  }

  exitPrequal() {
    stratum.publishTag(
      TagKey.ICONIC_VEHICLE_AND_LOAN_RETURN_TO_CAPITAL_ONE_BUTTON
    );
    document.location.href = 'https://www.capitalone.com/';
  }
}

customElements.define('refi-delayed-decline', RefiDelayedDecline);
